import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface PriorityItem {
	priority: number;
	label: string;
}

interface PriorityInputProps {
	value: string | number;
	onChange: (value: string) => void;
	/** List of existing items with their priorities to show in dropdown */
	existingItems?: PriorityItem[];
	/** ID of the current item being edited (to exclude from the list) */
	currentItemId?: string;
	min?: number;
	max?: number;
	placeholder?: string;
	disabled?: boolean;
	className?: string;
}

export const PriorityInput = ({
	value,
	onChange,
	existingItems = [],
	min = 0,
	max = 1000,
	placeholder,
	disabled = false,
	className
}: PriorityInputProps) => {
	const { t } = useTranslation('common');
	const [isOpen, setIsOpen] = useState(false);
	const [inputValue, setInputValue] = useState(value?.toString() ?? '');
	const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	const justSelectedRef = useRef(false);

	// Group items by priority and create display items with "+X more" suffix
	const sortedItems = useMemo(() => {
		// Filter valid items and sort by priority
		const validItems = [...existingItems]
			.filter(item =>
				item.priority != null &&
				!isNaN(item.priority) &&
				item.priority >= min &&
				item.priority <= max
			)
			.sort((a, b) => a.priority - b.priority);

		// Group by priority value
		const grouped = new Map<number, string[]>();
		for (const item of validItems) {
			const existing = grouped.get(item.priority);
			if (existing) {
				existing.push(item.label);
			} else {
				grouped.set(item.priority, [item.label]);
			}
		}

		// Convert to array with first label and count of additional items
		return Array.from(grouped.entries()).map(([priority, labels]) => ({
			priority,
			label: labels[0],
			additionalCount: labels.length - 1
		}));
	}, [existingItems, min, max]);

	// Sync input value with prop value
	useEffect(() => {
		setInputValue(value?.toString() ?? '');
	}, [value]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;

		// Allow empty value for clearing
		if (newValue === '') {
			setInputValue('');
			onChange('');
			return;
		}

		// Only allow numeric input (no negative signs, decimals, etc.)
		if (!/^\d*$/.test(newValue)) {
			return;
		}

		setInputValue(newValue);

		// Update the form value (validation happens in the form)
		onChange(newValue);
	};

	const handleInputBlur = () => {
		// On blur, ensure the value is valid and within range
		if (inputValue === '') {
			return;
		}

		const numValue = parseInt(inputValue, 10);
		if (isNaN(numValue)) {
			setInputValue(value?.toString() ?? '');
			return;
		}

		// Clamp to valid range on blur
		const clampedValue = Math.max(min, Math.min(max, numValue));
		setInputValue(clampedValue.toString());
		onChange(clampedValue.toString());
	};

	const handleItemSelect = (priority: number) => {
		setInputValue(priority.toString());
		onChange(priority.toString());
		setIsOpen(false);
		setHighlightedIndex(-1);
		// Set flag to prevent dropdown from reopening on focus
		justSelectedRef.current = true;
		inputRef.current?.focus();
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Escape') {
			setIsOpen(false);
			setHighlightedIndex(-1);
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (isOpen && sortedItems.length > 0) {
				// Navigate down in dropdown
				setHighlightedIndex(prev =>
					prev < sortedItems.length - 1 ? prev + 1 : prev
				);
			} else if (!isOpen) {
				// Increment value by 1 when dropdown is closed
				const currentVal = parseInt(inputValue, 10) || 0;
				const newVal = Math.min(max, currentVal + 1);
				setInputValue(newVal.toString());
				onChange(newVal.toString());
			}
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (isOpen && sortedItems.length > 0) {
				// Navigate up in dropdown
				setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
			} else if (!isOpen) {
				// Decrement value by 1 when dropdown is closed
				const currentVal = parseInt(inputValue, 10) || 0;
				const newVal = Math.max(min, currentVal - 1);
				setInputValue(newVal.toString());
				onChange(newVal.toString());
			}
		} else if (e.key === 'Enter' && isOpen && highlightedIndex >= 0) {
			e.preventDefault();
			const selectedItem = sortedItems[highlightedIndex];
			if (selectedItem) {
				handleItemSelect(selectedItem.priority);
			}
		}
	};

	// Reset highlighted index when dropdown opens/closes
	useEffect(() => {
		if (isOpen) {
			// Find the index of the current value in the list
			const currentVal = parseInt(inputValue, 10);
			const index = sortedItems.findIndex(item => item.priority === currentVal);
			setHighlightedIndex(index >= 0 ? index : 0);
		} else {
			setHighlightedIndex(-1);
		}
	}, [isOpen, sortedItems, inputValue]);

	// Scroll highlighted item into view
	useEffect(() => {
		if (isOpen && highlightedIndex >= 0 && listRef.current) {
			const buttons = listRef.current.querySelectorAll('button');
			const highlightedButton = buttons[highlightedIndex];
			if (highlightedButton) {
				highlightedButton.scrollIntoView({ block: 'nearest' });
			}
		}
	}, [highlightedIndex, isOpen]);

	return (
		<div ref={containerRef} className={cn('relative', className)}>
			<div className="relative">
				<input
					ref={inputRef}
					type="text"
					inputMode="numeric"
					pattern="[0-9]*"
					value={inputValue}
					onChange={handleInputChange}
					onBlur={handleInputBlur}
					onKeyDown={handleKeyDown}
					onFocus={() => {
						if (justSelectedRef.current) {
							justSelectedRef.current = false;
							return;
						}
						setIsOpen(true);
					}}
					placeholder={placeholder ?? t('priority.placeholder', '0-1000')}
					disabled={disabled}
					className={cn(
						'flex h-9 w-full rounded-md border border-input bg-[#fafafa] px-3 py-1 pr-10 text-base font-semibold shadow-xs transition-[color,box-shadow] file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-placeholder placeholder:font-light placeholder-shown:font-normal focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-slate-900'
					)}
				/>
				<button
					type="button"
					onClick={() => !disabled && setIsOpen(!isOpen)}
					disabled={disabled}
					title={t('priority.showExisting', 'Show existing priorities')}
					aria-label={t('priority.showExisting', 'Show existing priorities')}
					className="absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground hover:text-foreground disabled:opacity-50"
				>
					<ChevronDown
						className={cn(
							'h-4 w-4 transition-transform duration-200',
							isOpen && 'rotate-180'
						)}
					/>
				</button>
			</div>

			{/* Dropdown with existing priorities */}
			{isOpen && !disabled && (
				<div className="absolute z-50 w-full mt-1 overflow-hidden bg-popover border border-border rounded-md shadow-md animate-in fade-in-0 zoom-in-95">
					<div ref={listRef} className="max-h-60 overflow-y-auto">
						{sortedItems.length > 0 ? (
							sortedItems.map((item, index) => (
								<button
									key={`${item.priority}-${index}`}
									type="button"
									onClick={() => handleItemSelect(item.priority)}
									onMouseEnter={() => setHighlightedIndex(index)}
									className={cn(
										'w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2',
										highlightedIndex === index
											? 'bg-accent text-accent-foreground'
											: 'hover:bg-accent/60 hover:text-accent-foreground'
									)}
								>
									<span className="font-mono font-semibold min-w-[3ch] text-right shrink-0">
										{item.priority}
									</span>
									<span className="text-muted-foreground/60 shrink-0">—</span>
									<span className="text-muted-foreground truncate">
										{item.label}
									</span>
									{item.additionalCount > 0 && (
										<span className="text-muted-foreground/50 text-xs shrink-0">
											+{item.additionalCount} more
										</span>
									)}
								</button>
							))
						) : (
							<div className="px-3 py-4 text-sm text-center text-muted-foreground">
								{t('priority.noExisting', 'No existing priorities')}
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};
